import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Descriptions, Drawer, Grid, Input, Modal, Result, Segmented, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { ModuleIcon } from '@shared/common/components/ui/module-launcher';
import { PageHeader } from '@/components/PageHeader';
import {
  StudioModulesApiError,
  deleteModule,
  getModuleCatalog,
  reorderModules,
  type StudioModule,
  type StudioModuleCatalog,
} from '@/api/studioModules';
import { moduleGlyph } from '@/layouts/navigation';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { ModuleEditorDrawer, ScopeTag } from './ModuleEditorDrawer';
import { titleOf } from './moduleEditing';

const { Text } = Typography;

// Administration → Modules (route /platform/modules, nav key studio-modules). Every change goes
// through api/AgentStudioModules and persists; the server decides validity, versions and audit.
// Admin-only on the server: anyone else gets a 403 here, and their navigation never lists it.

type Filter = 'all' | 'shown' | 'hidden' | 'drafts';

/** Read by screen readers, invisible on screen (column headers that are icons only). */
const visuallyHidden: React.CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

export const StudioModulesPage = observer(function StudioModulesPage() {
  const { studioModuleStore: sm, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const screens = Grid.useBreakpoint();

  const [catalog, setCatalog] = useState<StudioModuleCatalog | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'forbidden'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<{ module: StudioModule | null } | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async (): Promise<StudioModuleCatalog | null> => {
    try {
      const next = await getModuleCatalog();
      setCatalog(next);
      setStatus('ready');
      setError(null);
      return next;
    } catch (err) {
      const e = err as StudioModulesApiError;
      setStatus(e.status === 403 ? 'forbidden' : 'error');
      setError(e.message);
      return null;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** After any change: this table and everyone's navigation (this tab's, at least) read the new catalog. */
  const afterChange = useCallback(async () => {
    await load();
    await sm.load();
  }, [load, sm]);

  const modules = useMemo(() => catalog?.modules ?? [], [catalog]);
  const q = query.trim().toLowerCase();
  const filtered = modules.filter((m) => {
    if (filter === 'shown' && !m.isVisible) return false;
    if (filter === 'hidden' && m.isVisible) return false;
    if (filter === 'drafts' && !m.isDraft) return false;
    return !q || `${m.title} ${m.moduleKey} ${m.description ?? ''}`.toLowerCase().includes(q);
  });
  // Reordering is over the WHOLE set; a filtered view cannot say where the hidden rows go.
  const canReorder = !q && filter === 'all' && !reordering;

  const reorder = async (next: StudioModule[]): Promise<void> => {
    setReordering(true);
    try {
      const result = await reorderModules(next.map((m) => ({ id: m.id, rowVersion: m.rowVersion })));
      if (result.kind === 'saved') void message.success('Module order saved.');
      else if (result.kind === 'conflict') void message.warning('Someone changed the modules meanwhile — showing their latest order. Try again.');
      else void message.error('message' in result ? result.message : result.kind === 'invalid' ? result.problems.map((p) => p.message).join(' ') : 'The order was not saved.');
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      await afterChange();
      setReordering(false);
    }
  };

  const moveBy = (module: StudioModule, delta: number): void => {
    const from = modules.findIndex((m) => m.id === module.id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= modules.length) return;
    // Keyboard users keep their place: focus returns to the moved row's button (or its twin at an end).
    void reorder(arrayMove(modules, from, to)).then(() =>
      window.setTimeout(() => {
        const label = (dir: string) => `button[aria-label="Move ${window.CSS.escape(module.title)} ${dir}"]`;
        const same = document.querySelector<HTMLButtonElement>(label(delta < 0 ? 'up' : 'down'));
        const twin = document.querySelector<HTMLButtonElement>(label(delta < 0 ? 'down' : 'up'));
        (same && !same.disabled ? same : twin)?.focus();
      }, 50),
    );
  };

  const confirmDelete = (module: StudioModule): void => {
    Modal.confirm({
      title: `Remove "${module.title}"?`,
      content: 'It holds no pages, so nothing moves and no one loses access. Its history is kept.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await deleteModule(module.id, module.rowVersion);
        if (result.kind === 'saved') void message.success(`Removed "${module.title}".`);
        else if (result.kind === 'invalid') void message.error(result.problems.map((p) => p.message).join(' '));
        else void message.error(result.message);
        await afterChange();
      },
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;
    const from = modules.findIndex((m) => m.id === active.id);
    const to = modules.findIndex((m) => m.id === over.id);
    if (from >= 0 && to >= 0) void reorder(arrayMove(modules, from, to));
  };

  if (status === 'forbidden') {
    return (
      <Result
        status="403"
        title="Only an Agent Studio admin can manage modules"
        subTitle="Modules decide how pages are grouped in navigation. Your own pages and permissions are not affected."
      />
    );
  }

  const client = sm.diagnostics;
  const server = catalog?.diagnostics;
  const driftCount =
    (server ? server.unknownNavKeys.length + server.unplacedNavKeys.length + server.invalidDefaults.length + (server.recoveryRouteHidden ? 1 : 0) : 0) +
    client.unsupportedKeys.length + client.clientOnlyKeys.length + client.routeMismatches.length + (client.manifestMismatch ? 1 : 0);

  const columns: ColumnsType<StudioModule> = [
    {
      title: <span style={visuallyHidden}>Order</span>,
      key: 'order',
      width: 120,
      render: (_, m) => {
        const index = modules.findIndex((x) => x.id === m.id);
        return (
          <Space size={2}>
            <DragHandle disabled={!canReorder} label={`Drag ${m.title} to reorder`} />
            <Text type="secondary" style={{ width: 18, textAlign: 'right', display: 'inline-block' }}>{index + 1}</Text>
            <Tooltip title={canReorder ? 'Move up' : 'Clear search and filters to reorder'}>
              <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={!canReorder || index === 0} onClick={() => moveBy(m, -1)} aria-label={`Move ${m.title} up`} />
            </Tooltip>
            <Tooltip title={canReorder ? 'Move down' : 'Clear search and filters to reorder'}>
              <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={!canReorder || index === modules.length - 1} onClick={() => moveBy(m, 1)} aria-label={`Move ${m.title} down`} />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Module',
      key: 'module',
      render: (_, m) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 240, maxWidth: 380 }}>
          <ModuleIcon glyph={moduleGlyph(m.iconKey)} color={m.color ?? undefined} size="sm" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{m.title}</div>
            {m.description && <Text type="secondary" style={{ fontSize: 12 }}>{m.description}</Text>}
          </div>
        </div>
      ),
    },
    {
      title: 'Navigation',
      key: 'visible',
      render: (_, m) => (
        <Space size={4} wrap>
          {m.isVisible ? <Tag icon={<EyeOutlined />} color="green">Shown</Tag> : <Tag icon={<EyeInvisibleOutlined />}>Hidden</Tag>}
          {m.isDraft && <Tag>Draft</Tag>}
        </Space>
      ),
    },
    {
      title: 'Pages',
      key: 'pages',
      align: 'right',
      render: (_, m) => (
        <Tooltip title={m.items.length ? m.items.map((i) => titleOf(i.navKey, catalog)).join(', ') : 'No pages — a draft'}>
          <span>{m.items.length}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Opens on',
      key: 'default',
      responsive: ['md'],
      render: (_, m) => (m.defaultNavKey ? titleOf(m.defaultNavKey, catalog) : <Text type="secondary">First page</Text>),
    },
    {
      title: 'Key',
      dataIndex: 'moduleKey',
      responsive: ['lg'],
      render: (k: string) => <Text code>{k}</Text>,
    },
    {
      title: 'Type',
      key: 'type',
      responsive: ['md'],
      render: (_, m) => (m.isSystem ? <Tag icon={<LockOutlined />}>System</Tag> : <Tag color="blue">Custom</Tag>),
    },
    {
      title: 'Last updated',
      key: 'updated',
      responsive: ['xl'],
      render: (_, m) =>
        m.lastUpdatedUtc ? (
          <Tooltip title={dayjs(m.lastUpdatedUtc).format('D MMM YYYY, HH:mm:ss')}>
            <span>{dayjs(m.lastUpdatedUtc).format('D MMM YYYY')}</span>
          </Tooltip>
        ) : '—',
    },
    {
      title: <span style={visuallyHidden}>Actions</span>,
      key: 'actions',
      fixed: screens.md ? 'right' : undefined,
      render: (_, m) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing({ module: m })} aria-label={`Edit ${m.title}`}>
            {screens.sm ? 'Edit' : null}
          </Button>
          {!m.isSystem && (
            <Tooltip title={m.items.length ? `Move its ${m.items.length} page(s) to other modules first` : 'Remove this custom module'}>
              <Button size="small" danger icon={<DeleteOutlined />} disabled={m.items.length > 0} onClick={() => confirmDelete(m)} aria-label={`Remove ${m.title}`} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        icon={<AppstoreOutlined />}
        accent="violet"
        title="Modules"
        subtitle="How Agent Studio's pages are grouped in the launcher and menus. Grouping never changes a page's URL, data scope or who may open it."
        count={modules.length || undefined}
        countLabel="modules"
        actions={
          <Space wrap>
            <Button icon={<InfoCircleOutlined />} onClick={() => setDetailsOpen(true)} disabled={!catalog}>
              Pages &amp; diagnostics{driftCount > 0 ? ` (${driftCount})` : ''}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({ module: null })} disabled={!catalog}>
              New module
            </Button>
          </Space>
        }
      />

      {status === 'error' && (
        <Alert
          type="error"
          showIcon
          role="alert"
          style={{ marginBottom: 16 }}
          message="Couldn't load the modules"
          description={error}
          action={<Button icon={<ReloadOutlined />} onClick={() => void load()}>Retry</Button>}
        />
      )}

      {driftCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="The catalog and this version of Agent Studio disagree about some pages"
          description="Pages either side does not know are never shown in navigation. Open Pages & diagnostics for the details."
          action={<Button size="small" onClick={() => setDetailsOpen(true)}>Details</Button>}
        />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <Input.Search placeholder="Search modules" allowClear value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 320 }} aria-label="Search modules" />
        <Segmented<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'shown', label: 'Shown' },
            { value: 'hidden', label: 'Hidden' },
            { value: 'drafts', label: 'Drafts' },
          ]}
          aria-label="Filter modules"
        />
        {!canReorder && !reordering && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Clear search and filters to reorder.
          </Text>
        )}
        {reordering && <Spin size="small" />}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={filtered.map((m) => m.id)} strategy={verticalListSortingStrategy}>
          <Table<StudioModule>
            rowKey="id"
            loading={status === 'loading'}
            columns={columns}
            dataSource={filtered}
            pagination={false}
            components={{ body: { row: SortableRow } }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: q || filter !== 'all' ? 'No module matches.' : 'No modules yet.' }}
            style={{ background: isDark ? colors.slate[800] : '#FFFFFF', borderRadius: 12 }}
          />
        </SortableContext>
      </DndContext>

      {catalog && editing && (
        <ModuleEditorDrawer
          open={!!editing}
          module={editing.module}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={afterChange}
          reloadCatalog={load}
        />
      )}

      {catalog && <DiagnosticsDrawer open={detailsOpen} onClose={() => setDetailsOpen(false)} catalog={catalog} />}
    </div>
  );
});

// ── Drag-sortable rows (antd Table + dnd-kit) ────────────────────────────────

const RowHandle = createContext<{ attributes: Record<string, unknown>; listeners: Record<string, unknown> | undefined; setActivatorNodeRef: (el: HTMLElement | null) => void } | null>(null);

function SortableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: string | number }) {
  const id = Number(props['data-row-key']);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9, background: 'rgba(124,58,237,0.06)' } : {}),
  };
  return (
    <RowHandle.Provider value={{ attributes: attributes as unknown as Record<string, unknown>, listeners: listeners as Record<string, unknown> | undefined, setActivatorNodeRef }}>
      <tr {...props} ref={setNodeRef} style={style} />
    </RowHandle.Provider>
  );
}

function DragHandle({ disabled, label }: { disabled: boolean; label: string }) {
  const ctx = useContext(RowHandle);
  if (!ctx || disabled) {
    return <Button size="small" type="text" icon={<HolderOutlined />} disabled aria-label={label} />;
  }
  return (
    <Button
      size="small"
      type="text"
      icon={<HolderOutlined />}
      ref={ctx.setActivatorNodeRef}
      style={{ cursor: 'grab', touchAction: 'none' }}
      aria-label={`${label} (space to pick up, arrows to move, space to drop)`}
      {...ctx.attributes}
      {...ctx.listeners}
    />
  );
}

// ── Diagnostics: the raw capability/policy picture, for admins ────────────────

const DiagnosticsDrawer = observer(function DiagnosticsDrawer({ open, onClose, catalog }: { open: boolean; onClose: () => void; catalog: StudioModuleCatalog }) {
  const { studioModuleStore: sm } = useStore();
  const screens = Grid.useBreakpoint();
  const client = sm.diagnostics;
  const d = catalog.diagnostics;
  const list = (keys: string[]) => (keys.length ? keys.map((k) => <Tag key={k}>{k}</Tag>) : <Text type="secondary">None</Text>);

  return (
    <Drawer open={open} onClose={onClose} size={screens.md ? 760 : '100%'} title="Pages & diagnostics">
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 20 }}>
        <Descriptions.Item label="Destination manifest">
          Server v{catalog.manifestVersion} · this build v{client.clientManifestVersion}
          {catalog.manifestVersion !== client.clientManifestVersion && <Tag color="orange" style={{ marginLeft: 8 }}>Mismatch — deploy in progress?</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Catalog revision"><Text code>{catalog.catalogRevision}</Text></Descriptions.Item>
        <Descriptions.Item label="Placed keys the server doesn't know">{list(d.unknownNavKeys)}</Descriptions.Item>
        <Descriptions.Item label="Pages in no module">{list(d.unplacedNavKeys)}</Descriptions.Item>
        <Descriptions.Item label="Defaults that aren't the module's own">{list(d.invalidDefaults)}</Descriptions.Item>
        <Descriptions.Item label="Modules page reachable">{d.recoveryRouteHidden ? <Tag color="red">No — its module is hidden or missing</Tag> : 'Yes'}</Descriptions.Item>
        <Descriptions.Item label="Server pages this build can't open">{list(client.unsupportedKeys)}</Descriptions.Item>
        <Descriptions.Item label="Pages in this build the server doesn't list">{list(client.clientOnlyKeys)}</Descriptions.Item>
        <Descriptions.Item label="Route disagreements">
          {client.routeMismatches.length ? client.routeMismatches.map((r) => <div key={r.navKey}><Text code>{r.navKey}</Text> server {r.server} · build {r.client}</div>) : <Text type="secondary">None</Text>}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Title level={5}>Registered pages</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12.5 }}>
        What each page requires is decided by its API, not by the module it sits in. Home is global and is not listed.
      </Text>
      <Table
        size="small"
        rowKey="navKey"
        pagination={false}
        dataSource={catalog.destinations}
        scroll={{ x: 640 }}
        columns={[
          { title: 'Key', dataIndex: 'navKey', render: (k: string) => <Text code>{k}</Text> },
          { title: 'Page', dataIndex: 'title' },
          { title: 'Route', dataIndex: 'route', render: (r: string) => <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{r}</Text> },
          { title: 'Data', dataIndex: 'scope', render: (s: string) => <ScopeTag scope={s} /> },
          { title: 'Requires', key: 'access', render: (_, r) => (r.access === 'admin' ? 'Admin account' : r.access === 'claim' ? `Claim ${r.claim}` : 'Signed-in Studio account') },
          { title: 'Module', key: 'owner', render: (_, r) => catalog.modules.find((m) => m.moduleKey === r.ownerModuleKey)?.title ?? <Text type="warning">None</Text> },
        ]}
      />
    </Drawer>
  );
});
